import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Calendar,
  Users,
  FileText,
  AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import useAuth from "../../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import Loader from "../../../common_components/Loader";
import { RecruitmentStatus, matchStatus } from "../../../types/status";
import { useToast } from "../../../common_components/Toast";

export interface Vacancy {
  categories?: Record<string, number>;
}

export interface Department {
  name: string;
  vacancies?: Vacancy[];
}

export interface Recruitment {
  _id: string;
  recruitmentId: string;
  title: string;
  status: string;
  description?: string;
  applicationDates?: {
    startDate?: string;
    endDate?: string;
  };
  departments?: Department[];
}

interface RecruitmentListPageProps {
  type: "FACULTY" | "NON_FACULTY";
}

export default function RecruitmentListPage({ type }: RecruitmentListPageProps) {
  const { apiRequest, isRecruitmentAdmin } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const isFaculty = type === "FACULTY";
  const endpointPrefix = isFaculty ? "/faculty" : "/non-faculty";
  const titleLabel = isFaculty ? "Faculty Recruitment" : "Non-Faculty Recruitment";

  const [recruitments, setRecruitments] = useState<Recruitment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showArchive, setShowArchive] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const fetchRecruitments = useCallback(
    async (pageNum = page, showArc = showArchive, showDel = showDeleted) => {
      setLoading(true);
      setError(null);
      try {
        const url = showDel
          ? `${endpointPrefix}/recruitment?page=${pageNum}&limit=10&showDeleted=true`
          : `${endpointPrefix}/recruitment?page=${pageNum}&limit=10&showArchive=${showArc}`;
        const data = await apiRequest(url, { method: "GET" });
        setRecruitments(data.recruitments || []);
        setTotalPages(data.totalPages || 1);
      } catch (err: any) {
        setError(err.message || "Failed to fetch recruitments");
        console.error("Error fetching recruitments:", err);
      } finally {
        setLoading(false);
      }
    },
    [apiRequest, endpointPrefix, page, showArchive, showDeleted]
  );

  useEffect(() => {
    fetchRecruitments(page, showArchive, showDeleted);
  }, [fetchRecruitments, page, showArchive, showDeleted]);

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      await apiRequest(`${endpointPrefix}/recruitment/${id}`, {
        method: "DELETE",
      });
      setDeleteConfirm(null);
      toast.success("Recruitment soft deleted successfully!");
      fetchRecruitments(page, showArchive, showDeleted);
    } catch (err: any) {
      console.error("Error deleting recruitment:", err);
      toast.error(err.message || "Failed to delete recruitment");
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    if (matchStatus(status, RecruitmentStatus.PUBLISHED)) return "bg-green-100 text-green-800 border-green-200";
    if (matchStatus(status, RecruitmentStatus.DRAFT)) return "bg-yellow-100 text-yellow-800 border-yellow-200";
    if (matchStatus(status, RecruitmentStatus.ARCHIVED)) return "bg-purple-100 text-purple-800 border-purple-200";
    if (matchStatus(status, RecruitmentStatus.DELETED)) return "bg-red-100 text-red-800 border-red-200";
    return "bg-blue-100 text-blue-800 border-blue-200";
  };

  const getTotalVacancies = (departments?: Department[]) => {
    if (!departments || departments.length === 0) return 0;
    return departments.reduce((total, dept) => {
      const deptTotal =
        dept.vacancies?.reduce((sum, vac) => {
          const categorySum = Object.values(vac.categories || {}).reduce(
            (catSum, count) => catSum + (count || 0),
            0
          );
          return sum + categorySum;
        }, 0) || 0;
      return total + deptTotal;
    }, 0);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif text-slate-900 mb-2 tracking-tight leading-tight">
              Recruitments
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
              Manage all {isFaculty ? "Faculty" : "Non-Faculty"} recruitment drives and vacancies
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isRecruitmentAdmin && (
              <label className="inline-flex items-center cursor-pointer gap-2 bg-red-50 hover:bg-red-100 px-4 py-3 rounded border border-red-200 shadow-sm transition-colors">
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => {
                    setShowDeleted(e.target.checked);
                    if (e.target.checked) setShowArchive(false);
                    setPage(1);
                  }}
                  className="rounded text-red-600 focus:ring-red-500 border-red-300 w-4 h-4 cursor-pointer"
                />
                <span className="text-sm font-semibold text-red-700">Show Deleted Items</span>
              </label>
            )}
            <label className="inline-flex items-center cursor-pointer gap-2 bg-white hover:bg-slate-100 px-4 py-3 rounded border border-slate-200 shadow-sm transition-colors">
              <input
                type="checkbox"
                checked={showArchive}
                onChange={(e) => {
                  setShowArchive(e.target.checked);
                  if (e.target.checked) setShowDeleted(false);
                  setPage(1);
                }}
                className="rounded text-slate-900 focus:ring-slate-900 border-slate-300 w-4 h-4 cursor-pointer"
              />
              <span className="text-sm font-semibold text-slate-700">Show Archive</span>
            </label>
            <button
              onClick={() => navigate(`${endpointPrefix}/admin/recruitment/apply`)}
              className="flex items-center gap-2 px-6 py-3 bg-slate-800 text-white rounded border border-slate-700 shadow-sm font-medium hover:bg-slate-900 transition-all"
            >
              <Plus className="w-5 h-5" />
              Create {titleLabel}
            </button>
          </div>
        </motion.div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 mb-1">
                Error Loading Recruitments
              </h3>
              <p className="text-red-700 text-sm">{error}</p>
              <button
                onClick={() => fetchRecruitments(page, showArchive, showDeleted)}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Try Again
              </button>
            </div>
          </motion.div>
        )}

        {recruitments.length === 0 && !error ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center"
          >
            <FileText className="w-20 h-20 text-slate-300 mx-auto mb-4" />
            <h2 className="text-2xl font-serif font-bold text-slate-900 mb-2">
              No Recruitments Yet
            </h2>
            <p className="text-slate-600 mb-6">
              Get started by creating your first recruitment drive
            </p>
            <button
              onClick={() => navigate(`${endpointPrefix}/admin/recruitment/apply`)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 text-white rounded border border-slate-700 shadow-sm font-medium hover:bg-slate-900 transition-all"
            >
              <Plus className="w-5 h-5" />
              Create First Recruitment
            </button>
          </motion.div>
        ) : (
          <div className="grid gap-6">
            {recruitments.map((recruitment, index) => (
              <motion.div
                key={recruitment._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow p-6"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-2">
                      <h2 className="text-2xl font-serif font-bold text-slate-900">
                        {recruitment.title}
                      </h2>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(
                          recruitment.status
                        )}`}
                      >
                        {recruitment.status}
                      </span>
                    </div>
                    {recruitment.description && (
                      <p className="text-slate-600 mb-4">
                        {recruitment.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">Start Date</p>
                      <p className="font-semibold text-gray-900">
                        {formatDate(recruitment.applicationDates?.startDate)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="text-xs text-gray-500">End Date</p>
                      <p className="font-semibold text-gray-900">
                        {formatDate(recruitment.applicationDates?.endDate)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-xs text-gray-500">Departments</p>
                      <p className="font-semibold text-gray-900">
                        {recruitment.departments?.length || 0}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="text-xs text-gray-500">Total Vacancies</p>
                      <p className="font-semibold text-gray-900">
                        {getTotalVacancies(recruitment.departments)}
                      </p>
                    </div>
                  </div>
                </div>

                {recruitment.departments &&
                  recruitment.departments.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                        Departments:
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {recruitment.departments.map((dept, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200"
                          >
                            {dept.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
                  <button
                    onClick={() =>
                      navigate(
                        `${endpointPrefix}/admin/recruitment/${recruitment.recruitmentId}/edit`
                      )
                    }
                    className="flex items-center gap-2 px-4 py-2 text-slate-700 bg-slate-100 border border-slate-200 rounded shadow-sm hover:bg-slate-200 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    View Details
                  </button>

                  {!matchStatus(recruitment.status, RecruitmentStatus.ARCHIVED) &&
                    !matchStatus(recruitment.status, RecruitmentStatus.DELETED) && (
                      <button
                        onClick={() =>
                          navigate(
                            `${endpointPrefix}/admin/recruitment/${recruitment.recruitmentId}/edit`
                          )
                        }
                        className="flex items-center gap-2 px-4 py-2 text-slate-700 bg-slate-100 border border-slate-200 rounded shadow-sm hover:bg-slate-200 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                    )}

                  <button
                    onClick={() =>
                      navigate(
                        `${endpointPrefix}/admin/${recruitment.recruitmentId}/applications/all`
                      )
                    }
                    className="flex items-center gap-2 px-4 py-2 text-slate-700 bg-slate-100 border border-slate-200 rounded shadow-sm hover:bg-slate-200 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    See Applications
                  </button>

                  {!matchStatus(recruitment.status, RecruitmentStatus.ARCHIVED) &&
                    !matchStatus(recruitment.status, RecruitmentStatus.DELETED) && (
                      <button
                        onClick={() => setDeleteConfirm(recruitment.recruitmentId)}
                        className="flex items-center gap-2 px-4 py-2 text-red-700 bg-red-50 border border-red-200 rounded shadow-sm hover:bg-red-100 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    )}

                  {matchStatus(recruitment.status, RecruitmentStatus.DELETED) && (
                    <span className="px-3 py-1.5 text-xs font-bold text-red-700 bg-red-100 border border-red-200 rounded">
                      DELETED
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 pt-6 mt-6">
            <p className="text-xs text-slate-500 font-medium">
              Showing page <span className="font-semibold text-slate-800">{page}</span> of{" "}
              <span className="font-semibold text-slate-800">{totalPages}</span>
            </p>
            <div className="flex gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                className="px-3 py-1.5 text-xs font-semibold bg-slate-900 border border-slate-900 rounded-lg hover:bg-slate-800 text-white disabled:opacity-50 disabled:pointer-events-none transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {deleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Delete Recruitment
                  </h3>
                  <p className="text-sm text-gray-600">
                    This action cannot be undone
                  </p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                Are you sure you want to delete this recruitment? All associated
                data will be permanently removed.
              </p>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}
